import axios from 'axios'
import i18n from '../lang'
import flights from './flights'
import claims from './claims'

// If io logging flag is present then we log each request and response to the console
if (process.env.IO_LOGGING_VERBOSE) {
  axios.interceptors.request.use(function (config) {
    console.log('---------------->> IO Request')
    console.log(config)
    return config
  }, function (error) {
    console.log('---------------->> IO Request Error')
    console.log(error)
    return Promise.reject(error)
  })

  axios.interceptors.response.use(function (response) {
    console.log('<<---------------- IO Response')
    console.log(response)
    return response
  }, function (error) {
    console.log('<<---------------- IO Response Error')
    console.log(error)
    return Promise.reject(error)
  })
}

const dataBasePath = '/static/theme/data'

export default {
  getAirports () {
    return this.requestData('airports')
  },
  getLanguages () {
    return axios.get(`/static/theme/data/languages.json`)
  },
  getClaimTypes () {
    return this.requestData('claim-types')
  },
  getCountries () {
    return this.requestData('countries')
      .then((response) => {
        return this._orderData(response.data, 'name')
      })
  },
  getPrivacyPolicy () {
    return this.requestData('privacy-policy')
  },
  getTermsAndConditions () {
    return this.requestData('termsandconditions')
  },
  getCurrencies () {
    return this.requestData('currencies')
  },
  getBaggageTypes () {
    return this.requestData('claims-item/baggage-types')
  },
  getBaggageBrands () {
    return this.requestData('claims-item/baggage-brands')
  },
  getBaggageColours () {
    return this.requestData('claims-item/baggage-colours')
  },
  getBaggageMaterials () {
    return this.requestData('claims-item/baggage-materials')
  },
  getBaggageWheels () {
    return this.requestData('claims-item/baggage-wheels')
  },
  getBaggageLocks () {
    return this.requestData('claims-item/baggage-locks')
  },
  getBaggageHeights () {
    return this.requestData('claims-item/baggage-heights')
  },
  getBaggagePrices () {
    return this.requestData('claims-item/baggage-prices')
  },
  getBaggageDamages () {
    return this.requestData('claims-item/baggage-damages')
  },
  /**
   * Find a flight
   * @param {number} date - The date, as an epoch, for which data should be returned.
   * @param {string} flightNumber - The flight number. Performs an exact match on the flight number regardless of
   * carrier code
   * @returns {Promise<FlightResult[]>} - A promise containing array of FlightResult
   */
  getFlight (date, flightNumber) {
    return flights.getFlight(date, flightNumber)
  },
  /**
   * POST a damage claim
   */
  postClaim (damageClaim) {
    return claims.postClaim(damageClaim)
  },

  /**
   * Get claim status
   * @param {string} guid - guid of claim
   */
  getClaimStatus (guid, code) {
    return claims.getClaimStatus(guid, code)
  },

  /**
   * Fetch the chat messages connected to a claim
   * @param {string} guid - guid for the claim
   * @param {string} code - the verification code for the claim
   */
  getClaimMessages (guid, code) {
    return claims.getClaimMessages(guid, code)
  },

  getClaimVerificationCode (guid) {
    return claims.getClaimVerificationCode(guid)
  },

  /**
   * Add a new chat message to a claim
   * @param {string} guid - guid for the claim
   * @param {stirng} message - chat message to add to the claim
   */
  postClaimMessage (guid, code, message) {
    return claims.postClaimMessage(guid, code, message)
  },

  /**
   * Add a new document to a claim
   * @param {string} guid - guid for the claim
   * @param {string} code - the verification code for the claim
   * @param {stirng} fileUrl - uploaded file url
   * @param {stirng} description - uploaded file description e.g. filename
   */
  postClaimDocument (guid, code, fileUrl, description) {
    return claims.postClaimDocument(guid, code, fileUrl, description)
  },

  /**
   * Post images update for claim
   * @param {string} guid - user's unique guid
   * @param {imageUpdate} imageUpdate - image update object containing images
   */
  postClaimImageUpdate (guid, imageUpdate, baggageItemId) {
    return claims.postClaimImageUpdate(guid, imageUpdate, baggageItemId)
  },

  /**
   * Posts details of the baggage items to get suitable replacement options
   * from the API (requires flight date and claim provider)
   * @param {StockReplacementRequest} replacementRequest - request payload containing baggageItem and claim info
   */
  getReplacementOptions (replacementRequest) {
    return claims.getReplacementOptions(replacementRequest)
  },

  /**
   * Post replacement status update for the claim
   * @param {string} guid - claim's unique id
   * @param {ReplacementStatusUpdate} replacementStatus - object containing status update
   */
  updateClaimReplacementStatus (guid, code, replacementStatus) {
    return claims.updateClaimReplacementStatus(guid, code, replacementStatus)
  },

  /**
   * POST luggage address confirmation object for the claim
   * @param {string} guid - claim's unique id
   * @param {LuggageAddressUpdate} address - address confirmation object
   */
  updateClaimLuggageAddress (guid, address) {
    return claims.updateClaimLuggageAddress(guid, address)
  },

  /**
   * Get reported claim details from the PIR number.
   * i.e. get the claim details from WorldTracer
   * @param {string} pir - pir reference
   */
  getAirportReport (pir) {
    return claims.getAirportReport(pir)
  },

  /**
   * Returns language data based on user's locale.
   * If given locale was not found, fallback locale is used instead.
   *
   * @param {string} endpoint - The endpoint of the language file
   * @returns {response} - HTTP response containing data of language to be used.
   */
  // Todo fix fallback not working when deployed
  requestData (endpoint) {
    return axios.get(`${dataBasePath}/${i18n.locale}/${endpoint}.json`)
      .catch(error => {
        if (error.response.status === 404) {
          // We pass the whole error response
          console.error(error.response)
          return axios.get(`${dataBasePath}/${i18n.fallbackLocale}/${endpoint}.json`)
        }
      })
  },

  /**
   * merges common and all data arrays together prioritising the common array
   *
   * @param {Array} data - array of all data to merge to order
   * @param {string} key - Key of item to match against common string
   */
  _orderData (data, key) {
    let array = []
    const common = data.common
    const all = data.all

    if (
      common &&
      common.length > 0
    ) {
      // Find data for each of the common data and
      // push into array
      common.forEach((commonDataItem) => {
        array.push(
          all.find((dataItem) => {
            return dataItem[key] === commonDataItem
          })
        )
      })
    }

    // concat common data with all data
    return array.concat(all)
  }
}
